<template>
  <div class="event-attendance-buttons" :class="{ noAnswer }" @dblclick.stop>
    <button
      class="going-button"
      :class="{ selected: event.willAttend === 'yes' }"
      @click.stop="updateWillAttend('yes')"
    >
      <GoingIcon class="icon" />
      <span v-if="event.willAttend === 'yes' || !compact" class="going-text">
        Yes
      </span>
    </button>
    <div class="separator" :class="{ noAnswer }" />
    <button
      class="decline-button"
      :class="{ selected: event.willAttend === 'no' }"
      @click.stop="updateWillAttend('no')"
    >
      <NotGoingIcon class="icon" />
      <span v-if="event.willAttend === 'no' || !compact" class="declined-text">
        No
      </span>
    </button>
    <div class="separator" :class="{ noAnswer }" />
    <button
      class="question-button"
      :class="{ selected: event.willAttend === 'maybe' }"
      @click.stop="updateWillAttend('maybe')"
    >
      <MaybeIcon class="icon" />
      <span
        v-if="event.willAttend === 'maybe' || !compact"
        class="tentative-text"
      >
        Maybe
      </span>
    </button>
    <div class="selected" />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  toRefs,
} from "vue";

import type { FullEvent, YesNoMaybe } from "@ctrl/controller-rpc";

import GoingIcon from "@/components/icons/GoingIcon.vue";
import MaybeIcon from "@/components/icons/MaybeIcon.vue";
import NotGoingIcon from "@/components/icons/NotGoingIcon.vue";

import { useEventInfo } from "./eventInfo";
import * as inject from "./inject";
import { safeUpdateEvent } from "./modal";

export default defineComponent({
  components: {
    NotGoingIcon,
    MaybeIcon,
    GoingIcon,
  },
  props: {
    compact: { type: Boolean, default: false },
    event: { type: Object as PropType<FullEvent>, required: true },
  },
  async setup(props) {
    const { event } = toRefs(props);
    const controller = inject.controller();
    const calendars = inject.calendars();
    const simpleModal = inject.simpleModal();
    const multipleEventsModal = inject.multipleEventsModal();
    const eventInfo = useEventInfo(controller, calendars, event);
    onBeforeUnmount(() =>
      eventInfo.then((eventInfo) => eventInfo.releaseEventInfo())
    );
    const { calendarOwnerEmail } = await eventInfo;
    const updateWillAttend = (value: YesNoMaybe) => {
      let forceNotify = false;
      const owner = event.value.creator?.email;
      if (owner && owner !== calendarOwnerEmail.value) {
        forceNotify = true;
      }
      safeUpdateEvent(
        "update",
        event.value.id,
        {
          controller,
          simpleModal,
          multipleEventsModal,
        },
        {
          //FIXME: fix following behaviour
          multipleEventsChoices: { this: true, following: false, all: true },
          patchWillAttend: value,
          forceNotify,
        }
      );
    };
    const noAnswer = computed<boolean>(() => event.value.willAttend === null);
    return {
      noAnswer,
      updateWillAttend,
    };
  },
});
</script>

<style lang="scss" scoped>
.event-attendance-buttons {
  display: flex;
  flex-direction: row;
  padding: 4px;
  border-radius: 5px;
  width: fit-content;
  justify-content: space-between;

  @include themed() {
    background: t($grey-05);
  }

  .decline-button,
  .going-button,
  .question-button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    padding: 4px 12px;
    column-gap: 6px;
    @extend %font-caption-medium;
    @include transition(background-color 100ms ease-out, color 100ms ease-out);
    @include themed() {
      color: t($grey-50);
      background: t($grey-05);
    }

    &:hover {
      @include themed() {
        background-color: t($grey-15);
      }
    }

    svg.icon {
      height: 8px;
      width: 8px;
    }

    &.selected {
      @include themed() {
        background-color: t($bg);
      }
    }
  }

  .going-button {
    &:hover,
    &.selected {
      color: $accepted;

      :deep(svg) path {
        &.stroke {
          stroke: $accepted;
        }

        &.fill {
          fill: $accepted;
        }
      }
    }
  }

  .question-button {
    &:hover,
    &.selected {
      @include themed() {
        color: t($grey-70);
      }

      :deep(svg) path {
        &.stroke {
          @include themed() {
            background-color: t($grey-70);
          }
        }

        &.fill {
          @include themed() {
            background-color: t($grey-70);
          }
        }
      }
    }
  }

  .decline-button {
    &:hover,
    &.selected {
      color: $declined;

      :deep(svg) path {
        &.stroke {
          stroke: $declined;
        }

        &.fill {
          fill: $declined;
        }
      }
    }
  }

  .separator {
    width: 1px;
    background-color: transparent;
    margin: 3px 0;

    &.noAnswer {
      @include themed() {
        background-color: t($grey-15);
      }
    }
  }
}
</style>
